import React, { useMemo, useState } from 'react';
import { FormGroup, Button } from 'src/components/ui';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';

import useResourceLoader from '../../util/useResourceLoader';
import Notification from '../Notification';
import Loader from '../Loader';
import FileApi from '../../api/FileApi';
import StaticFileApi from '../../api/StaticFileApi';
import FileInput from '../FileInput';
import Jnx, { parseJnxExpr, useJnx } from '../../util/jnx';
import { useSideChannelSubscription } from '../../util/useSideChannel';
import getPathFromId from '../../util/getPathFromId';
import useTestId from '../../hooks/useTestId';

function FileField(props) {
  const {
    formData,
    registry,
    name,
    disabled,
    readonly: propReadonly,
    schema: {
      title,
      buttonText,
      emptyText,
      fileUrlField = 'url',
      fileNameField = 'name',
      resource = t`File`,
      accept = '*',
      type, // has to be number (the file id)
      const: constValue,
      staticFile,
      wfAccessToken: schemaWFAccessToken,
    },
    fieldPathId: { $id: fieldId, path: fieldPath },
    uiSchema: {
      'ui:titleAsLink': titleAsLink,
      'ui:hideOnEmpty': hideOnEmpty,
      'ui:readonly': uiReadonly,
      'ui:inlinePreview': uiShowPreview,
      'ui:hideAttr': uiHideAttr,
      'akc:requiredIfVisible': requiredIfVisible,
      'akc:requiredIf': requiredIfExpr,
    },
    // title,
    required,
    onChange: propOnChange,
  } = props;
  const formContext = registry?.formContext;
  const { setFormDataValues, sideChannel } = formContext || {};
  const labelId = `${fieldId}-label`;
  const postArgs = ((formContext || {}).fileField || {}).postArgs;
  const testIdProps = useTestId(fieldId);
  const wfAccessToken = useMemo(() => formContext?.wfAccessToken ?? schemaWFAccessToken ?? {}, [formContext?.wfAccessToken, schemaWFAccessToken]);

  const useFileId = type === 'number';
  const useStaticFile = !!staticFile;
  const value = formData || constValue || staticFile;
  const defaultValue = '';
  const dataValue = formData || defaultValue;
  const rootFormData = useSideChannelSubscription(sideChannel, 0);

  const readonly = uiReadonly || !!constValue || useStaticFile || propReadonly;
  const dataPath = useMemo(() => getPathFromId(fieldId), [fieldId]);
  // console.log(requiredIfExpr)
  // console.log(REQUIRED_IF_JNX(requiredIfExpr))
  const requiredIfJnx = useJnx(requiredIfExpr);
  const requiredIf = useMemo(
    () =>
      requiredIfJnx &&
      requiredIfJnx.eval(rootFormData || {}, dataPath, {
        root: rootFormData,
        formContext,
      }),
    [dataValue, rootFormData, formContext]
  );
  const requiredFile = !!required || !!requiredIfVisible || !!requiredIf;

  const [file, loadingFile, errorLoadingFile] = useResourceLoader(() => {
    if (useFileId) return value ? FileApi.getFile({ fileId: value, wfAccessToken }) : {};
    if (useStaticFile) return StaticFileApi.getFile({ key: staticFile });

    if (!!value && typeof value === 'object') {
      if (value.url && value.name) return value;

      return { url: value[fileUrlField], name: value[fileNameField] };
    } else {
      return {
        url: value,
        name: (value || '')
          .split('/')
          .pop()
          .split('?')[0],
      };
    }
  }, [useFileId, value, wfAccessToken]);

  const [uploadingFile, setUploadingFile] = useState();
  const [errorUploadingFile, setErrorUploadingFile] = useState();
  const error =
    errorLoadingFile ||
    errorUploadingFile ||
    (!loadingFile && (!file || (readonly && !file.url)) ? { message: emptyText || t`No ${resource} to show`, alert: 'secondary' } : null);

  async function onChange(file) {
    if (file) {
      const extension = file.name.indexOf('.') >= 0 ? `.${file.name.split('.').pop()}` : '';

      try {
        setUploadingFile(true);
        const result = await FileApi.uploadFile({ ...(postArgs || {}), file, filename: `${name}${extension}`, wfAccessToken });
        propOnChange(useFileId ? result.id : result.url, fieldPath);
      } catch (e) {
        setErrorUploadingFile(e);
      }

      setUploadingFile(false);
    }
  }

  if (hideOnEmpty && readonly && !value) {
    return null;
  }

  if (uiHideAttr && readonly) {
    return null;
  }

  return (
    <FormGroup className="file-field" disabled={readonly}>
      {titleAsLink && title && readonly && file && file.url ? (
        <Button tag="a" href={file.url} target="_blank" {...testIdProps}>
          {title}
        </Button>
      ) : (
        <>
          <>
            {error ? <Notification error={error} /> : null}
            <FileInput
              fieldTitle={title}
              file={file}
              disabled={disabled}
              viewButtonText={buttonText}
              onFileSelected={onChange}
              resource={resource}
              accept={accept}
              readonly={readonly}
              inlinePreview={uiShowPreview}
              required={requiredFile}
              uploadingFile={uploadingFile}
              loadingFile={loadingFile}
              {...testIdProps}
            />
          </>
        </>
      )}
    </FormGroup>
  );
}

export default FileField;
